// #753 — Admin Health Concerns sub-page. Two sections:
//   A. Confirmed concerns — memories.category='health_concern', active
//      first then resolved (lighter block).
//   B. Pending audit trail — pending_health_concerns rows for this user,
//      newest first, full lifecycle visible (open + confirmed + denied +
//      expired_*).
// Single GET round-trip returns both. The wipe action nukes ALL of the
// above for staging reruns; gated by the same email-typing prompt as
// the existing delete-account flow (AdminUserDetail.js #228).
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { adminApi, adminGet, adminPost } from './adminApi';
import { userSubPageStyles, fmtDateTime } from './userSubPageStyles';

const VERSION = 'v1.0.0';

// Page-local additions on top of the shared sub-page styles —
// .data-grid / .data-table / .page-header / .back-link / .section-title /
// .empty-state / .loading-state / .error-state / .ver / .section-sub
// all live in userSubPageStyles. Here we add: the resolved-block
// fade, the uuid cell, the outcome badges, and the danger-zone +
// wipe button.
const styles = `
  ${userSubPageStyles}
  .resolved-block { opacity: 0.7; }
  .resolved-block .data-grid { box-shadow: 0 1px 6px rgba(0,0,0,0.05); }
  .uuid-cell {
    font-family: 'SF Mono', Menlo, monospace;
    font-size: 0.75rem; color: #888;
  }
  .outcome-badge {
    display: inline-block; padding: 2px 8px; border-radius: 99px;
    font-size: 0.72rem; font-weight: 700; text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  .outcome-pending   { background: #FFF8E0; color: #B08C00; }
  .outcome-confirmed { background: #F0FFF4; color: #1F8A4A; }
  .outcome-denied    { background: #F0F0F0; color: #666; }
  .outcome-expired   { background: #FBEEEA; color: #B84A2B; }
  .danger-zone {
    margin-top: 40px; padding: 20px 24px;
    background: #FFF8F7; border: 1px solid #F4D3CB;
    border-radius: 12px;
  }
  .danger-zone h3 {
    font-size: 0.95rem; font-weight: 700; color: #C0392B;
    margin-bottom: 6px;
  }
  .danger-zone p {
    font-size: 0.85rem; color: #666; margin-bottom: 14px;
    line-height: 1.45;
  }
  .btn-wipe {
    padding: 9px 18px; border-radius: 8px;
    font-family: inherit; font-size: 0.85rem; font-weight: 600;
    border: 2px solid #C0392B; background: #fff; color: #C0392B;
    cursor: pointer; transition: background 0.15s, transform 0.15s;
  }
  .btn-wipe:hover { background: #C0392B; color: #fff; transform: scale(1.02); }
  .btn-wipe:disabled { opacity: 0.5; cursor: not-allowed; }
`;

function outcomeBadge(outcome) {
  if (!outcome) {
    return <span className="outcome-badge outcome-pending">pending</span>;
  }
  if (outcome === 'confirmed') {
    return <span className="outcome-badge outcome-confirmed">confirmed</span>;
  }
  if (outcome === 'denied') {
    return <span className="outcome-badge outcome-denied">denied</span>;
  }
  if (outcome.startsWith('expired')) {
    return (
      <span className="outcome-badge outcome-expired" title={outcome}>
        {outcome.replace(/^expired_?/, 'expired ') || 'expired'}
      </span>
    );
  }
  return <span className="outcome-badge">{outcome}</span>;
}

function ConfirmedTable({ rows }) {
  if (!rows || rows.length === 0) {
    return <div className="empty-state">No confirmed health concerns.</div>;
  }
  return (
    <div className="data-grid">
      <table className="data-table">
        <thead>
          <tr>
            <th>Key</th>
            <th>Value</th>
            <th>Subtype</th>
            <th>Created</th>
            <th>Last follow-up</th>
            <th>Resolved</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id}>
              <td style={{ color: '#888' }}>{r.key}</td>
              <td>{r.value}</td>
              <td style={{ color: '#888' }}>{r.subtype || '—'}</td>
              <td style={{ color: '#888', whiteSpace: 'nowrap' }}>{fmtDateTime(r.created_at)}</td>
              <td style={{ color: '#888', whiteSpace: 'nowrap' }}>{fmtDateTime(r.last_followed_up_at)}</td>
              <td style={{ color: '#888', whiteSpace: 'nowrap' }}>{fmtDateTime(r.resolved_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PendingTable({ rows }) {
  if (!rows || rows.length === 0) {
    return <div className="empty-state">No pending audit entries.</div>;
  }
  return (
    <div className="data-grid">
      <table className="data-table">
        <thead>
          <tr>
            <th>Candidate key</th>
            <th>Candidate value</th>
            <th>Verbatim phrase</th>
            <th>Created</th>
            <th>Asked</th>
            <th>Outcome</th>
            <th>Outcome at</th>
            <th>Committed memory</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id}>
              <td style={{ color: '#888' }}>{r.candidate_key}</td>
              <td>{r.candidate_value}</td>
              <td style={{ color: '#666', fontStyle: 'italic' }}>
                {r.verbatim_phrase ? `"${r.verbatim_phrase}"` : '—'}
              </td>
              <td style={{ color: '#888', whiteSpace: 'nowrap' }}>{fmtDateTime(r.created_at)}</td>
              <td style={{ color: '#888', whiteSpace: 'nowrap' }}>{fmtDateTime(r.asked_at)}</td>
              <td>{outcomeBadge(r.outcome)}</td>
              <td style={{ color: '#888', whiteSpace: 'nowrap' }}>{fmtDateTime(r.outcome_at)}</td>
              <td className="uuid-cell">{r.committed_memory_id || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminUserHealthConcerns() {
  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [wiping, setWiping] = useState(false);

  async function fetchAll() {
    setLoading(true);
    setError(null);
    try {
      // Two parallel requests: user info (for header + the email gate)
      // and the health-concerns payload (single round-trip, both sections).
      const [u, hc] = await Promise.all([
        adminApi.getUser(id),
        adminGet(`/users/${id}/health-concerns`),
      ]);
      setUser(u);
      setData(hc);
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchAll(); }, [id]);  // eslint-disable-line react-hooks/exhaustive-deps

  async function wipe() {
    const expected = (user && user.email) || '';
    const typed = window.prompt(
      `This permanently DELETES ALL health-concern data for ${expected}:\n` +
      `- every confirmed concern in long-term memory\n` +
      `- every pending audit row (entire verify-before-commit history)\n\n` +
      `This cannot be undone. Type the user's email exactly to confirm:`
    );
    if (typed === null) return;
    if (typed.trim().toLowerCase() !== expected.trim().toLowerCase()) {
      alert("Email didn't match — wipe aborted.");
      return;
    }
    setWiping(true);
    try {
      const res = await adminPost(`/users/${id}/health-concerns/wipe`);
      alert(
        `Wiped: ${res.memories_deleted} confirmed concern(s), ` +
        `${res.pending_deleted} pending audit row(s).`
      );
      await fetchAll();
    } catch (e) {
      alert('Error: ' + e.message);
    } finally {
      setWiping(false);
    }
  }

  // Split confirmed rows into active vs resolved so the resolved block
  // can render in a lighter visual style under its own header.
  const confirmed = data?.confirmed || [];
  const activeConfirmed = confirmed.filter(r => !r.resolved_at);
  const resolvedConfirmed = confirmed.filter(r => r.resolved_at);

  return (
    <AdminLayout title="Health Concerns">
      <style>{styles}</style>

      <Link to={`/admin/users/${id}`} className="back-link">← Back to user</Link>

      {loading && <p className="loading-state">Loading…</p>}
      {error && <p className="error-state">Error: {error}</p>}

      {user && (
        <div className="page-header">
          <div className="page-title">
            {user.name || '(unnamed)'}<span className="ver">{VERSION}</span>
          </div>
          <div className="page-subtitle">
            {user.email} · Health concerns sub-page
          </div>
        </div>
      )}

      {data && (
        <>
          <div className="section-title">
            Confirmed concerns
            <span className="section-sub">
              memories.category = 'health_concern' · {activeConfirmed.length} active
              {resolvedConfirmed.length > 0 && `, ${resolvedConfirmed.length} resolved`}
            </span>
          </div>
          <ConfirmedTable rows={activeConfirmed} />

          {resolvedConfirmed.length > 0 && (
            <div className="resolved-block">
              <div className="section-title" style={{ fontSize: '0.85rem', color: '#888' }}>
                Resolved
              </div>
              <ConfirmedTable rows={resolvedConfirmed} />
            </div>
          )}

          <div className="section-title">
            Pending audit trail
            <span className="section-sub">
              pending_health_concerns · {data.pending.length} row(s),
              newest first {data.pending.length >= 200 && '· capped at 200'}
            </span>
          </div>
          <PendingTable rows={data.pending} />

          <div className="danger-zone">
            <h3>Wipe all health concerns</h3>
            <p>
              Permanently deletes every confirmed health-concern memory AND
              the entire pending audit trail for this user. Intended for
              clean staging reruns of the verify-before-commit flow (#735).
              An audit row is written to <code>security_events</code>.
            </p>
            <button
              className="btn-wipe"
              onClick={wipe}
              disabled={wiping || !user}
            >
              {wiping ? 'Wiping…' : 'Wipe all health concerns'}
            </button>
          </div>
        </>
      )}

      {!loading && !data && !error && (
        <p className="loading-state">No data.</p>
      )}
    </AdminLayout>
  );
}
